import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api, unwrap } from '@/lib/api';
import { addBreadcrumb } from '@/lib/sentry';
import { safeFormatDate, getDateFnsLocale } from '@/lib/dateUtils';
import { useTranslation } from '@/hooks/useTranslation';
import type { VatPaymentDirection } from '@releve/shared/schemas/vatSettlement';
import type {
  FileVatReturnSafeResult,
  RecordVatSettlementPaymentSafeResult,
  FileAndPayVatReturnSafeResult,
  VoidVatSettlementPaymentSafeResult,
  VoidVatReturnFilingSafeResult,
} from '@releve/shared/types/database-rpc';

// ---------------------------------------------------------------------------
// Row shape returned by GET /api/v1/vat-settlements (joined with business,
// settlement transaction, and payments).
// ---------------------------------------------------------------------------

export interface VatSettlementBankAccountRef {
  id: string;
  account_name: string;
}

export interface VatSettlementTransactionRef {
  id: string;
  transaction_number: string;
}

export interface VatSettlementPaymentRow {
  id: string;
  payment_amount: number;
  payment_date: string;
  payment_reference: string | null;
  paid_at: string;
  /** When set, the payment was voided (reversed) — kept for the audit trail. */
  voided_at: string | null;
  /**
   * Which way the money actually moved, stored on the row rather than derived
   * from the settlement's sign: a naheffingsaanslag pays back a settlement
   * that was in a refund position, so the sign of `net_due` is not the
   * direction of every payment against it.
   */
  direction: 'pay' | 'refund';
  /**
   * Set on both halves of an **offset** — one settlement's remaining netted
   * against another's instead of moving money through a bank account. The two
   * rows share this id and their payment journal; `bank_account` is null.
   */
  offset_group_id: string | null;
  /** Null on an offset half (no money moved through a bank account). */
  bank_account: VatSettlementBankAccountRef | null;
  payment_transaction: VatSettlementTransactionRef | null;
}

export interface VatSettlementRow {
  id: string;
  organization_id: string;
  period_start: string;
  period_end: string;
  output_vat_amount: number;
  input_vat_amount: number;
  net_due: number;
  /** Whole-unit filing only (e.g. NL); null when the return was filed exact. */
  filed_output_amount: number | null;
  filed_input_amount: number | null;
  /** Exact net minus filed net — the plug posted to the vat_rounding account. */
  rounding_amount: number | null;
  amount_paid: number;
  amount_remaining: number;
  tax_authority_business_id: string;
  filing_date: string;
  filing_reference: string | null;
  settlement_transaction_id: string | null;
  status: 'nil_return' | 'filed' | 'partially_paid' | 'paid' | 'refunded' | 'overpaid' | 'voided';
  /**
   * `'original'` = the period's regular return (at most one live row per
   * period). `'supplementary'` = a suppletie correcting periods already
   * covered by live originals.
   */
  kind: 'original' | 'supplementary';
  notes: string | null;
  filed_at: string;
  updated_at: string;
  /** When set, the filing was undone (reversed) — kept for the audit trail. */
  voided_at: string | null;
  void_reason: string | null;
  tax_authority: { id: string; name: string } | null;
  settlement_transaction: VatSettlementTransactionRef | null;
  payments: VatSettlementPaymentRow[];
}

/**
 * Magnitude of what is still open between the org and the tax authority —
 * `|amount_remaining|`, where `amount_remaining` is the generated
 * `net_due - amount_paid`.
 *
 * The sign carries the *direction*, not the existence, of the balance: a
 * positive remaining on a payable return is still owed, and a remaining whose
 * sign has flipped past zero (an over-refund, or a naheffingsaanslag) is owed
 * back the other way. Both are live amounts, so this returns the magnitude and
 * leaves the direction to the caller (every call site pairs it with an
 * "(owed back)" / "(due to you)" suffix keyed off `sign(amount_remaining)`).
 *
 * Deliberately **not** clamped at 0: the old clamp displayed an over-refunded
 * settlement as fully settled at all three call sites (history column, history
 * panel, payments panel) while the authority was waiting to be paid back.
 */
export function settlementOutstanding(
  s: Pick<VatSettlementRow, 'net_due' | 'amount_remaining'>,
): number {
  return Math.abs(s.amount_remaining);
}

/**
 * Statuses that can still take a payment or an offset: everything except a
 * nil return (nothing to settle) and a voided filing (the reversal cleared
 * it). An over-refunded or already-settled return can still take a repayment
 * or be reopened by a correction. One definition, shared by the pay dialog's
 * counterpart picker, the history row action and the carry-forward list, so a
 * future status cannot be payable in one place and invisible in another.
 */
export function isPayableStatus(status: VatSettlementRow['status']): boolean {
  return status !== 'nil_return' && status !== 'voided';
}

/**
 * The direction a settlement implies for its next payment: the sign of what
 * is still remaining, falling back to the sign of `net_due` once nothing is
 * remaining. Mirrors the RPC's default for `p_direction`, so leaving the
 * dialog's radio alone records exactly what the server would derive. An
 * over-refunded return (`net_due` < 0, remaining > 0) therefore points to
 * `pay` — money goes back.
 */
export function rowDirection(
  s: Pick<VatSettlementRow, 'net_due' | 'amount_remaining'>,
): VatPaymentDirection {
  const basis = s.amount_remaining !== 0 ? s.amount_remaining : s.net_due;
  return basis >= 0 ? 'pay' : 'refund';
}

/**
 * Which way a settlement's *remaining* balance points, but only once it has
 * flipped past zero relative to `net_due`: an over-refund the org has to pay
 * back ("owed back"), or an overpaid liability the authority owes back ("due
 * to you"). That flip is exactly what makes a settlement `overpaid`. A net-0
 * filing (whole-euro rounding can land there) with any payment on it has
 * flipped by definition.
 *
 * A remaining that still points the way the return did needs no note — the
 * Net due column already carries "(refund)" — so this returns null for it.
 */
export function remainingSuffixKey(
  s: Pick<VatSettlementRow, 'net_due' | 'amount_remaining'>,
): 'tax.history.owedBack' | 'tax.history.dueToYou' | null {
  if (s.amount_remaining === 0) return null;
  if (s.net_due !== 0 && Math.sign(s.amount_remaining) === Math.sign(s.net_due)) return null;
  return s.amount_remaining > 0 ? 'tax.history.owedBack' : 'tax.history.dueToYou';
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export function useVatSettlements(
  organizationId: string | undefined,
  options?: {
    from?: string;
    to?: string;
    /**
     * Combined with `!!organizationId` (default true). Lets a caller that only
     * *sometimes* needs the list — `useDeclaredVatPeriod` on every expanded
     * row — keep the fetch off until it has something to look up.
     */
    enabled?: boolean;
  },
) {
  return useQuery({
    queryKey: ['vatSettlements', organizationId, options?.from, options?.to],
    queryFn: async () => {
      if (!organizationId) return [] as VatSettlementRow[];
      const query: Record<string, string> = { organization_id: organizationId };
      if (options?.from) query.from = options.from;
      if (options?.to) query.to = options.to;
      const res = await api.api.v1['vat-settlements'].$get({
        query,
        header: { 'x-org-id': organizationId },
      });
      return unwrap<VatSettlementRow[]>(res);
    },
    enabled: !!organizationId && (options?.enabled ?? true),
    staleTime: 60_000,
  });
}

/**
 * The filed period a declared document belongs to, as one display label —
 * `"1 Jan 2026 – 31 Mar 2026"` (localized `PP` dates, en dash). The banner
 * and the expanded-panel notes both read it from here so they can never
 * disagree on the wording of a period.
 *
 * Resolved from the cached settlements list (one fetch per org, shared with
 * the tax pages) rather than a per-id endpoint, and **enabled only when
 * `settlementId` is set**: every expanded row asks, but only a declared one
 * pays for the list. A document can carry lines tagged to two settlements
 * (supplementary sweep); the id passed in names the one the label shows —
 * see `declaredSettlementIdOf` in `@/lib/vatDeclared`.
 *
 * `containingDate` (a `YYYY-MM-DD` calendar day) is the other way in, used
 * when no line carries a settlement tag: the LIVE filed return whose period
 * contains that day (the web mirror of `_vat_period_filed`; an original
 * before a supplementary) — e.g. an issued 0% AR invoice dated in a filed
 * period (`ar_invoice_index.in_filed_vat_period`). `settlementId` wins when
 * both are given.
 *
 * Null while the list is loading, when neither is set, or when the
 * settlement is not in the list (RLS hid it, or it was voided seconds ago);
 * callers render nothing in that case rather than a half-formed period.
 */
export function useDeclaredVatPeriod(
  organizationId: string | undefined,
  settlementId: string | null | undefined,
  containingDate?: string | null,
): { label: string } | null {
  const { i18n } = useTranslation();
  const { data: settlements } = useVatSettlements(organizationId, {
    enabled: !!settlementId || !!containingDate,
  });

  return useMemo(() => {
    if ((!settlementId && !containingDate) || !settlements) return null;
    const day = containingDate?.slice(0, 10);
    const settlement = settlementId
      ? settlements.find((s) => s.id === settlementId)
      : [...settlements]
          .filter((s) => s.voided_at == null && s.status !== "voided")
          .sort((a, b) => (a.kind === "original" ? 0 : 1) - (b.kind === "original" ? 0 : 1))
          .find((s) => !!day && s.period_start.slice(0, 10) <= day && s.period_end.slice(0, 10) >= day);
    if (!settlement) return null;
    // Month names in the user's language (date-fns `PP` = localized medium
    // date), same formatter as the refusal toast in useExpenses.
    const locale = getDateFnsLocale(i18n.language);
    const start = safeFormatDate(settlement.period_start, 'PP', '—', locale);
    const end = safeFormatDate(settlement.period_end, 'PP', '—', locale);
    return { label: `${start} – ${end}` };
  }, [settlementId, containingDate, settlements, i18n.language]);
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

/**
 * Cache fan-out for every mutation that posts or reverses a settlement or
 * payment JE (file, file-and-pay, record payment, void filing, void payment).
 * The posting touches the ledger views and the balance sheet, and — under
 * whole-unit filing — the vat_rounding plug line also moves the P&L, so the
 * income-statement/dashboard queries built on get_pnl_account_totals are
 * invalidated too.
 */
function invalidateSettlementJeCaches(qc: QueryClient, organizationId: string) {
  void qc.invalidateQueries({ queryKey: ['vatSettlements', organizationId] });
  void qc.invalidateQueries({ queryKey: ['taxReport', organizationId] });
  void qc.invalidateQueries({ queryKey: ['ledgerTransactions', organizationId] });
  // Filing tags every VAT line of the period (`ledger_entries.vat_settlement_id`)
  // and voiding untags them, which flips the declared flag the slim index rows
  // (`vat_declared_settlement_id`) and the loaded details gate their edit
  // actions on. Those must flip within seconds of a filing/void — an expanded
  // panel still offering Delete on a just-declared expense, or a form still
  // frozen after a void, is the liveness contract broken
  // (docs/INDEX_GRID_PATTERN.md). The expense index + detail, the AR index,
  // and the journal's lazily-loaded entries are the readers.
  void qc.invalidateQueries({ queryKey: ['expenses', organizationId] });
  void qc.invalidateQueries({ queryKey: ['expense'] });
  void qc.invalidateQueries({ queryKey: ['accountsReceivable', organizationId] });
  void qc.invalidateQueries({ queryKey: ['ledgerTransactionEntries'] });
  // The per-account register (bank overview) is a view of the ledger.
  void qc.invalidateQueries({ queryKey: ['accountTransactionsWithBalance', organizationId] });
  void qc.invalidateQueries({ queryKey: ['accountBalances'] });
  // get_pnl_account_totals consumers — the rounding plug is real income/expense.
  void qc.invalidateQueries({ queryKey: ['incomeStatementData', organizationId] });
  void qc.invalidateQueries({ queryKey: ['multiProjectIncomeStatement', organizationId] });
  void qc.invalidateQueries({ queryKey: ['dashboard', 'periodFlow', organizationId] });
  // The same JE moves the balance sheet (VAT accounts, settlement liability,
  // and the plug inside Current Year Net Income).
  void qc.invalidateQueries({ queryKey: ['balanceSheetData', organizationId] });
  // A first whole-unit filing creates the VAT Rounding Differences account on
  // demand (get_or_create_vat_rounding_account), so account lists and the
  // balances merged into them must refetch too.
  void qc.invalidateQueries({ queryKey: ['chartOfAccounts', organizationId] });
}

interface FileVatReturnInput {
  organization_id: string;
  period_start: string;
  period_end: string;
  filing_date: string;
  tax_authority_business_id: string;
  filing_reference?: string | null;
  notes?: string | null;
  /** Whole-unit filing (e.g. NL): declared amounts in whole currency units. Both or neither. */
  filed_output_amount?: number | null;
  filed_input_amount?: number | null;
  /** Defaults to `'original'` server-side; `'supplementary'` files a suppletie. */
  kind?: 'original' | 'supplementary';
  /**
   * Earlier settlements whose full remaining is netted against this return in
   * the same transaction ("correct it on the next aangifte"). Each id records
   * an offset pair, so this row's remaining becomes what the authority will
   * actually pay or collect.
   */
  carry_forward_settlement_ids?: string[] | null;
}

export function useFileVatReturn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: FileVatReturnInput) => {
      addBreadcrumb('VAT return file initiated', {
        period_start: input.period_start,
        period_end: input.period_end,
      });
      const res = await api.api.v1['vat-settlements'].file.$post({
        json: input,
        header: { 'x-org-id': input.organization_id },
      });
      return unwrap<FileVatReturnSafeResult>(res);
    },
    onSuccess: (_data, input) => {
      invalidateSettlementJeCaches(qc, input.organization_id);
    },
  });
}

interface RecordVatPaymentInput {
  organization_id: string; // for cache-key invalidation; not sent in body
  settlement_id: string;
  payment_amount: number;
  payment_date: string;
  /** Exactly one of `bank_account_id` / `offset_settlement_id` (schema-enforced). */
  bank_account_id?: string;
  payment_reference?: string | null;
  notes?: string | null;
  /**
   * Which way the money moved. Omitted, the server derives it from the sign of
   * the settlement's remaining; **required** for an offset.
   */
  direction?: 'pay' | 'refund';
  /** Counterpart settlement when netting instead of moving money through a bank. */
  offset_settlement_id?: string;
}

export function useRecordVatSettlementPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: RecordVatPaymentInput) => {
      addBreadcrumb('VAT settlement payment initiated', {
        settlement_id: input.settlement_id,
        payment_amount: input.payment_amount,
      });
      const { settlement_id, organization_id: _, ...body } = input;
      const res = await api.api.v1['vat-settlements'][':id'].pay.$post({
        param: { id: settlement_id },
        json: body,
        header: { 'x-org-id': input.organization_id },
      });
      return unwrap<RecordVatSettlementPaymentSafeResult>(res);
    },
    onSuccess: (_data, input) => {
      // The payment JE moves the bank account, the settlement liability, the
      // register, and the balance sheet — same fan-out as voiding it.
      invalidateSettlementJeCaches(qc, input.organization_id);
    },
  });
}

interface VoidVatPaymentInput {
  organization_id: string;
  payment_id: string;
  void_reason?: string | null;
}

/**
 * Void (delete) a recorded VAT settlement payment. Posts a reversing journal
 * entry, marks the payment voided, and recomputes the settlement. Invalidates
 * the settlements list plus the tax report / ledger / balances the reversal
 * touches (same fan-out as the bank-feed undo).
 */
export function useVoidVatSettlementPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: VoidVatPaymentInput) => {
      addBreadcrumb('VAT settlement payment void initiated', {
        payment_id: input.payment_id,
      });
      const res = await api.api.v1['vat-settlements'].payments[':paymentId'].void.$post({
        param: { paymentId: input.payment_id },
        json: { void_reason: input.void_reason ?? null },
        header: { 'x-org-id': input.organization_id },
      });
      return unwrap<VoidVatSettlementPaymentSafeResult>(res);
    },
    onSuccess: (_data, input) => {
      invalidateSettlementJeCaches(qc, input.organization_id);
    },
  });
}

interface VoidVatReturnFilingInput {
  organization_id: string;
  settlement_id: string;
  void_reason?: string | null;
}

/**
 * Undo (void) a filed VAT return that has no live payments. Posts a reversing
 * journal entry for the settlement JE, untags the period's VAT entries so the
 * period can be re-filed, and marks the settlement voided. Same cache fan-out
 * as voiding a payment — the reversal touches the ledger and reopens the
 * period in the tax report.
 */
export function useVoidVatReturnFiling() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: VoidVatReturnFilingInput) => {
      addBreadcrumb('VAT return filing void initiated', {
        settlement_id: input.settlement_id,
      });
      const res = await api.api.v1['vat-settlements'][':id'].void.$post({
        param: { id: input.settlement_id },
        json: { void_reason: input.void_reason ?? null },
        header: { 'x-org-id': input.organization_id },
      });
      return unwrap<VoidVatReturnFilingSafeResult>(res);
    },
    onSuccess: (_data, input) => {
      invalidateSettlementJeCaches(qc, input.organization_id);
    },
  });
}

interface FileAndPayVatReturnInput extends FileVatReturnInput {
  payment_amount: number;
  payment_date: string;
  bank_account_id: string;
  payment_reference?: string | null;
}

export function useFileAndPayVatReturn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: FileAndPayVatReturnInput) => {
      addBreadcrumb('VAT file-and-pay initiated', {
        period_start: input.period_start,
        period_end: input.period_end,
        payment_amount: input.payment_amount,
      });
      const res = await api.api.v1['vat-settlements']['file-and-pay'].$post({
        json: input,
        header: { 'x-org-id': input.organization_id },
      });
      return unwrap<FileAndPayVatReturnSafeResult>(res);
    },
    onSuccess: (_data, input) => {
      invalidateSettlementJeCaches(qc, input.organization_id);
    },
  });
}
